import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import interiorLibraryAdminApi from "../services/interiorLibraryAdminApi";
import interiorCatalogApi from "../services/interiorCatalogApi";

import { canDefinitionContain } from "../utils/definitionRules";
import {
  addChildToInstanceTree,
  createChildInstanceFromDefinition,
} from "../engine/instanceTreeUtils";

import ModuleChildrenTreePanel from "../components/definition-editor/ModuleChildrenTreePanel";
import HierarchySceneCanvas from "../components/definition-editor/HierarchySceneCanvas";
import DefinitionParametersInspector from "../components/definition-editor/DefinitionParametersInspector";
import DefinitionPropertiesInspector from "../components/definition-editor/DefinitionPropertiesInspector";
import AddChildDialog from "../components/definition-editor/AddChildDialog";
import RightPanel from "../components/definition-editor/RightPanel";
import ParameterManagerModal from "../components/definition-editor/ParameterManagerModal";
import { validateParameters } from "../utils/parameterSystem";
import { resolveNodeTree } from "../engine/resolveNodeTree";

function normalizeDefinition(item) {
  return {
    id: item.id,
    code: item.code,
    name: item.name,
    nodeType: item.node_type,
    category: item.category,
    catalogItemId: item.catalog_item_id || null,
    linkedItem: item.linked_item || null,
    linkedMounting: item.linked_mounting || null,
    description: item.description,
    status: item.status,
    defaultParameters: item.default_parameters || {},
    geometryType: item.geometry_type || "none",
    geometryDefaults: item.geometry_defaults || {},
    frontFaceAxis: item.front_face_axis || "+z",
    anchors: item.anchors || [],
    children: item.children || [],
    profile: item.profile || null,
    sketchPlane: item.sketch_plane || "xy",
  };
}

function enrichLinkedItemFromCatalog(definition, catalogItems = []) {
  const linkedItem = definition?.linkedItem || null;
  if (!linkedItem?.id) return definition;

  const catalogItem = (catalogItems || []).find(
    (item) => String(item.id) === String(linkedItem.id)
  );

  if (!catalogItem) return definition;

  const preservedComponents =
    linkedItem.components ||
    linkedItem.item_components ||
    catalogItem.components ||
    [];

  const preservedItemMounting =
    linkedItem.item_mounting ||
    linkedItem.itemMounting ||
    catalogItem.item_mounting ||
    catalogItem.itemMounting ||
    null;

  return {
    ...definition,

    linkedItem: {
      // prvo catalog osnovni podaci
      ...catalogItem,

      // zatim linkedItem iz definition API-ja, jer je on resolver-enriched
      ...linkedItem,

      asset:
        linkedItem.asset ||
        catalogItem.asset ||
        catalogItem.primary_asset ||
        catalogItem.linked_asset ||
        catalogItem.linkedAsset ||
        null,

      components: preservedComponents,
      item_mounting: preservedItemMounting,
      itemMounting: preservedItemMounting,
    },
  };
}

function enrichDefinitionLinkedItem(definition, catalogItems = []) {
  const linkedItem = definition?.linkedItem || null;

  if (!linkedItem?.id) return definition;

  const catalogItem = (catalogItems || []).find(
    (item) => String(item.id) === String(linkedItem.id)
  );

  if (!catalogItem) return definition;

  return {
    ...definition,
    linkedItem: {
      ...linkedItem,
      ...catalogItem,

      // VAŽNO: čuvamo asset ako ga catalog endpoint vraća
      asset:
        linkedItem.asset ||
        catalogItem.asset ||
        catalogItem.primary_asset ||
        catalogItem.linked_asset ||
        null,
    },
  };
}

function isBoardDefinition(def) {
  return (
    def &&
    def.nodeType === "element" &&
    String(def.category || "").startsWith("board.")
  );
}

function updateNodeInTree(nodes, nodeId, updater) {
  return (nodes || []).map((node) => {
    if (String(node.id) === String(nodeId)) return updater(node);

    return {
      ...node,
      children: updateNodeInTree(node.children || [], nodeId, updater),
    };
  });
}

function removeNodeFromTree(nodes, nodeId) {
  return (nodes || [])
    .filter((node) => String(node.id) !== String(nodeId))
    .map((node) => ({
      ...node,
      children: removeNodeFromTree(node.children || [], nodeId),
    }));
}

function findNodeById(nodes = [], id) {
  for (const node of nodes || []) {
    if (String(node.id) === String(id)) return node;
    const found = findNodeById(node.children || [], id);
    if (found) return found;
  }
  return null;
}

function nodeTypeBadgeClass(type) {
  switch (type) {
    case "module":
      return "bg-purple-100 text-purple-700";
    case "element":
      return "bg-blue-100 text-blue-700";
    case "hardware":
      return "bg-amber-100 text-amber-700";
    default:
      return "bg-gray-100 text-gray-700";
  }
}

function definitionIcon(def) {
  if (def.nodeType === "module") return "📦";
  if (def.nodeType === "hardware") return "⚙️";
  if (String(def.category || "").startsWith("board.")) return "🟫";
  if (String(def.category || "").startsWith("front.")) return "🚪";
  return "⬛";
}

function TabButton({ active, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`px-3 py-2 text-sm border-b-2 whitespace-nowrap ${
        active
          ? "border-blue-600 text-blue-700 bg-blue-50 font-medium"
          : "border-transparent text-slate-500 hover:text-slate-800"
      }`}
    >
      {children}
    </button>
  );
}

function HeaderButton({ children, onClick, danger, disabled }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`px-3 py-1.5 rounded border text-sm disabled:opacity-40 ${
        danger
          ? "bg-white text-red-600 border-slate-200 hover:bg-red-50"
          : "bg-white text-slate-700 border-slate-200 hover:bg-slate-50"
      }`}
    >
      {children}
    </button>
  );
}

function LinkedCatalogCard({ item, navigate }) {
  if (!item) {
    return (
      <div className="border rounded-lg p-4 text-sm text-slate-500 bg-slate-50">
        No catalog item selected.
      </div>
    );
  }

  return (
    <div className="border rounded-lg p-3 text-sm bg-slate-50 space-y-3">
      <div>
        <div className="font-semibold">{item.name}</div>
        <div className="text-xs text-slate-500">{item.code}</div>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs">
        <div>
          <div className="text-slate-500">Brand</div>
          <div>{item.brand || item.manufacturer || "—"}</div>
        </div>
        <div>
          <div className="text-slate-500">Asset</div>
          <div>{item.asset_id || "—"}</div>
        </div>
        <div>
          <div className="text-slate-500">SKU</div>
          <div>{item.sku || "—"}</div>
        </div>
        <div>
          <div className="text-slate-500">Status</div>
          <div>{item.status || "—"}</div>
        </div>
      </div>

      <div className="flex gap-2 flex-wrap">
        <HeaderButton onClick={() => navigate("/interior-editor/catalog")}>
          Open Catalog
        </HeaderButton>

        <HeaderButton
          onClick={() =>
            navigate(`/interior-editor/catalog/items/${item.id}/mounting`)
          }
        >
          Edit Mounting
        </HeaderButton>
      </div>
    </div>
  );
}

function DefinitionPicker({
  groupedDefinitions,
  definitionSearch,
  setDefinitionSearch,
  onAdd,
  onOpen,
}) {
  const sections = [
    ["Modules", groupedDefinitions.module],
    ["Elements", groupedDefinitions.element],
    ["Hardware / Processing", groupedDefinitions.hardware],
  ];

  return (
    <div className="h-full min-h-0 flex flex-col bg-white">
      <div className="p-3 border-b space-y-2">
        <input
          value={definitionSearch}
          onChange={(e) => setDefinitionSearch(e.target.value)}
          placeholder="Search components..."
          className="w-full px-3 py-2 border rounded text-sm bg-white"
        />
      </div>

      <div className="flex-1 min-h-0 overflow-auto p-3 space-y-4">
        {sections.map(([title, items]) =>
          items.length ? (
            <div key={title} className="space-y-2">
              <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                {title}
              </div>

              {items.slice(0, 40).map((def) => (
                <div
                  key={def.id}
                  className="rounded-lg border bg-white p-2 space-y-2 hover:bg-slate-50"
                >
                  <button
                    type="button"
                    onClick={() => onAdd(def)}
                    className="w-full text-left text-sm font-medium"
                  >
                    <span className="mr-1">{definitionIcon(def)}</span>
                    {def.name || def.code}
                  </button>

                  <div className="text-xs text-slate-500 truncate">
                    {def.code}
                  </div>

                  <button
                    type="button"
                    onClick={() => onOpen(def.id)}
                    className="text-xs text-blue-600"
                  >
                    Open / Edit
                  </button>
                </div>
              ))}
            </div>
          ) : null
        )}
      </div>
    </div>
  );
}

function flattenResolvedTree(nodes = [], acc = []) {
  (nodes || []).forEach((node) => {
    acc.push(node);
    flattenResolvedTree(node.children || [], acc);
  });

  return acc;
}

export default function ModuleDefinitionHierarchyPage() {
  const navigate = useNavigate();
  const { definitionId } = useParams();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [definition, setDefinition] = useState(null);
  const [allDefinitions, setAllDefinitions] = useState([]);
  const [children, setChildren] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [resolvedNode, setResolvedNode] = useState(null);
  const [selectedPartition, setSelectedPartition] = useState(null);

  const [editName, setEditName] = useState("");
  const [editCode, setEditCode] = useState("");
  const [editDescription, setEditDescription] = useState("");
  const [editNodeType, setEditNodeType] = useState("element");
  const [editCategory, setEditCategory] = useState("");
  const [editGeometryType, setEditGeometryType] = useState("none");
  const [editDefaultParameters, setEditDefaultParameters] = useState({});

  const [catalogItems, setCatalogItems] = useState([]);
  const [editCatalogItemId, setEditCatalogItemId] = useState("");
  const [catalogSearch, setCatalogSearch] = useState("");
const [catalogSearching, setCatalogSearching] = useState(false);

  const [activeDefinitionTab, setActiveDefinitionTab] = useState("general");
  const [leftTab, setLeftTab] = useState("tree");
  const [definitionSearch, setDefinitionSearch] = useState("");
  const [showAddChildDialog, setShowAddChildDialog] = useState(false);

  const [showParameterManager, setShowParameterManager] = useState(false);
  const [showMountingDrillings, setShowMountingDrillings] = useState(false);
  const [showCoordinateSystems, setShowCoordinateSystems] = useState(true);

  const [dimensionDraft, setDimensionDraft] = useState(null);

  const [formValidation, setFormValidation] = useState({
  ok: true,
  errors: [],
});

  const PREVIEW_PROJECT_PARAMETERS = {
  boardThickness: 18,
  backThickness: 8,
  defaultCabinetDepth: 560,

  cabinetInnerWidth: 564,
  runnerSpanWidth: 564,
  drawerBoxWidth: 546,
  drawerBoxHeight: 179,
  drawerWidth: 546,
  drawerDepth: 540,
  runnerLength: 550,
  runnerHeight: 42.8,
  sideClearance: 9,
  drawerDepthReduction: 10,
  minimumCabinetDepth: 553,
};

  function normalizeItemList(res) {
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.items)) return res.items;
  return [];
}

function onDimensionPick(pick) {
  if (!pick) return;

  setDimensionDraft((current) => {
    if (!current?.firstPoint) {
      return {
        firstPoint: pick,
        secondPoint: null,
      };
    }

    return {
      ...current,
      secondPoint: pick,
    };
  });
}

async function searchCatalogItems(searchText = "") {
  try {
    setCatalogSearching(true);

    const res = await interiorCatalogApi.listFamilyItems({
      search: searchText.trim(),
      limit: 50,
      offset: 0,
    });

    setCatalogItems(normalizeItemList(res));
  } catch (err) {
    console.error("catalog item search error:", err);
  } finally {
    setCatalogSearching(false);
  }
}

  async function load() {
    try {
      setLoading(true);

      const [currentRes, allRes, itemsRes] = await Promise.all([
        interiorLibraryAdminApi.getDefinition(definitionId),
        interiorLibraryAdminApi.listDefinitions(),
        interiorCatalogApi.listFamilyItems({ limit: 200 }),
      ]);

      const catalogList = Array.isArray(itemsRes)
  ? itemsRes
  : Array.isArray(itemsRes?.items)
  ? itemsRes.items
  : [];

const rawCurrent = normalizeDefinition(currentRes.item);
const rawDefs = (allRes.items || []).map(normalizeDefinition);

const current = enrichLinkedItemFromCatalog(rawCurrent, catalogList);

const defs = rawDefs.map((def) =>
  enrichLinkedItemFromCatalog(def, catalogList)
);

const mergedDefs = [
  current,
  ...defs.filter((d) => String(d.id) !== String(current.id)),
];

      setDefinition(current);
      setChildren(Array.isArray(current.children) ? current.children : []);
      setAllDefinitions(mergedDefs);

      setCatalogItems(catalogList);

      setEditCatalogItemId(current.catalogItemId || "");
      setEditName(current.name || "");
      setEditCode(current.code || "");
      setEditDescription(current.description || "");
      setEditNodeType(current.nodeType || "element");
      setEditCategory(current.category || "");
      setEditGeometryType(current.geometryType || "none");
      setEditDefaultParameters(current.defaultParameters || {});
      setSelectedId(null);
      setResolvedNode(null);
    } catch (err) {
      console.error("load hierarchy definition error:", err);
      alert("Failed to load definition.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [definitionId]);

  useEffect(() => {
  if (activeDefinitionTab !== "catalog") return;

  const timer = setTimeout(() => {
    searchCatalogItems(catalogSearch);
  }, 350);

  return () => clearTimeout(timer);
}, [catalogSearch, activeDefinitionTab]);

  const selectedNode = useMemo(
    () => findNodeById(children, selectedId),
    [children, selectedId]
  );

 const selectedCatalogItem = useMemo(() => {
  return (
    catalogItems.find((x) => String(x.id) === String(editCatalogItemId)) ||
    definition?.linkedItem ||
    null
  );
}, [catalogItems, editCatalogItemId, definition]);

const catalogSelectItems = useMemo(() => {
  const list = catalogItems || [];

  if (
    selectedCatalogItem?.id &&
    !list.some((x) => String(x.id) === String(selectedCatalogItem.id))
  ) {
    return [selectedCatalogItem, ...list];
  }

  return list;
}, [catalogItems, selectedCatalogItem]);

  const filteredCatalogItems = useMemo(() => {
  const q = catalogSearch.trim().toLowerCase();

  if (!q) return catalogItems;

  return (catalogItems || []).filter((item) => {
    return (
      String(item.name || "").toLowerCase().includes(q) ||
      String(item.code || "").toLowerCase().includes(q) ||
      String(item.sku || "").toLowerCase().includes(q) ||
      String(item.brand || "").toLowerCase().includes(q) ||
      String(item.manufacturer || "").toLowerCase().includes(q)
    );
  });
}, [catalogItems, catalogSearch]);

  const availableDefinitions = useMemo(() => {
    const q = definitionSearch.trim().toLowerCase();

    return allDefinitions.filter((d) => {
      if (String(d.id) === String(definition?.id)) return false;
      if (!canDefinitionContain(editNodeType, d.nodeType)) return false;

      if (!q) return true;

      return (
        String(d.name || "").toLowerCase().includes(q) ||
        String(d.code || "").toLowerCase().includes(q) ||
        String(d.category || "").toLowerCase().includes(q) ||
        String(d.nodeType || "").toLowerCase().includes(q)
      );
    });
  }, [allDefinitions, definition, editNodeType, definitionSearch]);

  const groupedDefinitions = useMemo(
    () => ({
      module: availableDefinitions.filter((d) => d.nodeType === "module"),
      element: availableDefinitions.filter((d) => d.nodeType === "element"),
      hardware: availableDefinitions.filter((d) => d.nodeType === "hardware"),
    }),
    [availableDefinitions]
  );

  const sceneRootInstances = useMemo(() => {
    if (!definition) return children || [];

    return [
      {
        id: "__definition_preview__",
        definitionId: definition.id,
        name: definition.name || definition.code || "Preview",
        transform: {
          position: { x: 0, y: 0, z: 0 },
          rotation: { x: 0, y: 0, z: 0 },
        },
        parameterBindings: {},
        parameterOverrides: {},
        children: children || [],
      },
    ];
  }, [definition, children]);

  const resolvedPreviewTree = useMemo(() => {
  if (!definition || !sceneRootInstances.length || !allDefinitions.length) {
    return [];
  }

  return resolveNodeTree({
    rootInstances: sceneRootInstances,
    definitions: allDefinitions,
    globalParameters: PREVIEW_PROJECT_PARAMETERS,
  });
}, [definition, sceneRootInstances, allDefinitions]);

const resolvedFlat = useMemo(
  () => flattenResolvedTree(resolvedPreviewTree),
  [resolvedPreviewTree]
);

const visibleResolvedNodes = useMemo(() => {
  return resolvedFlat.filter(
    (node) => String(node.id) !== "__definition_preview__"
  );
}, [resolvedFlat]);

  function openDefinitionEditorById(defId) {
    const def = allDefinitions.find((d) => String(d.id) === String(defId));
    if (!def) return;

    if (isBoardDefinition(def)) {
      navigate(`/interior-editor/definitions/${def.id}/board`);
    } else {
      navigate(`/interior-editor/definitions/${def.id}/hierarchy`);
    }
  }

  function openResolvedNodeEditor(node, preferred = "auto") {
  if (!node) return;

  const nodeType = String(node.nodeType || "").toLowerCase();

  if (preferred === "definition" || (preferred === "auto" && node.definitionId)) {
    if (node.definitionId) {
      openDefinitionEditorById(node.definitionId);
      return;
    }
  }

  if (nodeType === "item_assembly_frame") {
    const itemId = node.source?.itemId || node.item_mounting?.item_id;

    if (itemId) {
      navigate(`/interior-editor/catalog/items/${itemId}/assembly`);
      return;
    }

    alert("This item assembly frame has no linked source item.");
    return;
  }

  if (nodeType === "asset_component") {
    const sourceItemId = node.source?.itemId;
    const componentItemId = node.component_item?.id;
    const assetId = node.asset?.id;

    if (preferred === "component-item-mounting" && componentItemId) {
      navigate(`/interior-editor/catalog/items/${componentItemId}/mounting`);
      return;
    }

    if (preferred === "asset-mounting" && assetId) {
      navigate(`/interior-editor/assets/${assetId}/mounting`);
      return;
    }

    if (sourceItemId) {
      navigate(`/interior-editor/catalog/items/${sourceItemId}/assembly`);
      return;
    }

    if (componentItemId) {
      navigate(`/interior-editor/catalog/items/${componentItemId}/mounting`);
      return;
    }

    if (assetId) {
      navigate(`/interior-editor/assets/${assetId}/mounting`);
      return;
    }

    alert("This generated component has no source item link.");
    return;
  }

  if (nodeType === "mounting_item") {
    const scope = node.source?.mountingScope;
    const parentAssetId = node.source?.parentAssetId;
    const parentComponentItemId = node.source?.parentComponentItemId;
    const itemId = node.source?.itemId;
    const assetId = node.source?.assetId;

    if (scope === "asset_mounting" && parentAssetId) {
      navigate(`/interior-editor/assets/${parentAssetId}/mounting`);
      return;
    }

    if (scope === "item_mounting" && parentComponentItemId) {
      navigate(`/interior-editor/catalog/items/${parentComponentItemId}/mounting`);
      return;
    }

    if (itemId) {
      navigate(`/interior-editor/catalog/items/${itemId}/mounting`);
      return;
    }

    if (assetId) {
      navigate(`/interior-editor/assets/${assetId}/mounting`);
      return;
    }

    alert("This mounting item has no editable source link.");
    return;
  }

  if (node.definitionId) {
    openDefinitionEditorById(node.definitionId);
  }
}

  function updateSelectedNode(nextNode) {
    setChildren((prev) => updateNodeInTree(prev, nextNode.id, () => nextNode));
  }

  function addChildByDefinition(defItem) {
    if (!definition) return;

    if (String(defItem.id) === String(definition.id)) {
      alert("Definition cannot contain itself.");
      return;
    }

    if (!canDefinitionContain(editNodeType, defItem.nodeType)) {
      alert(
        `Child type "${defItem.nodeType}" is not allowed inside "${editNodeType}".`
      );
      return;
    }

    const childInstance = createChildInstanceFromDefinition(defItem);
    setChildren((prev) => [...prev, childInstance]);
    setSelectedId(childInstance.id);
    setResolvedNode(null);
    setLeftTab("tree");
  }

  function handleOpenAddChild() {
    setShowAddChildDialog(true);
  }

  function handlePickChildDefinition(defItem) {
    const childInstance = createChildInstanceFromDefinition(defItem);

    if (selectedNode) {
      setChildren((prev) =>
        addChildToInstanceTree(prev, selectedNode.id, childInstance)
      );
    } else {
      setChildren((prev) => [...prev, childInstance]);
    }

    setSelectedId(childInstance.id);
    setResolvedNode(null);
    setShowAddChildDialog(false);
    setLeftTab("tree");
  }

 function removeSelectedChild() {
  if (!selectedId) return;

  const manualNode = findNodeById(children, selectedId);

  if (!manualNode) {
    const resolved = resolvedNode || findResolvedNodeById(resolvedPreviewTree, selectedId);

    if (resolved) {
      alert(
        "This element is generated from linked catalog item / mounting. Use Open Source to edit or remove it from the catalog assembly or mounting editor."
      );
      return;
    }

    return;
  }

  const confirmed = window.confirm("Remove selected child from hierarchy?");
  if (!confirmed) return;

  setChildren((prev) => removeNodeFromTree(prev, selectedId));
  setSelectedId(null);
  setResolvedNode(null);
}

  function findResolvedNodeById(tree = [], id) {
  for (const node of tree || []) {
    if (String(node.id) === String(id)) return node;
    const found = findResolvedNodeById(node.children || [], id);
    if (found) return found;
  }
  return null;
}

  async function handleSave() {
    if (!definition) return;

    const validation = validateParameters({
  project: PREVIEW_PROJECT_PARAMETERS,
  type: editDefaultParameters,
  bindings: selectedNode?.parameterBindings || {},
  constraints: selectedNode?.constraints || {},
  instance: selectedNode?.parameterOverrides || {},
  parent: PREVIEW_PROJECT_PARAMETERS,
});

if (!validation.ok) {
  setFormValidation(validation);
  return;
}

setFormValidation({ ok: true, errors: [] });

    try {
      setSaving(true);

      const nextGeometryDefaults =
        editGeometryType === "box"
          ? { width: "width", height: "height", depth: "depth" }
          : {};

      await interiorLibraryAdminApi.updateDefinition(definition.id, {
        code: editCode,
        name: editName,
        node_type: editNodeType,
        category: editCategory,
        catalog_item_id: editCatalogItemId || null,
        description: editDescription,
        status: definition.status,
        default_parameters: editDefaultParameters || {},
        geometry_type: editGeometryType || "none",
        geometry_defaults: nextGeometryDefaults,
        front_face_axis: definition.frontFaceAxis || "+z",
        anchors: definition.anchors || [],
        children,
      });

      await load();
    } catch (err) {
      console.error("save hierarchy definition error:", err);
      alert("Save failed.");
    } finally {
      setSaving(false);
    }
  }

  async function handleDeleteDefinition() {
    if (!definition) return;

    const confirmed = window.confirm(`Delete definition "${definition.name}"?`);
    if (!confirmed) return;

    try {
      await interiorLibraryAdminApi.deleteDefinition(definition.id);
      navigate("/interior-editor/library");
    } catch (err) {
      console.error("delete definition error:", err);
      alert("Delete failed.");
    }
  }

  function openMountingEditor() {
  const itemId =
    editCatalogItemId ||
    definition?.catalogItemId ||
    definition?.catalog_item_id ||
    definition?.linkedItem?.id ||
    definition?.linked_item?.id;

  if (!itemId) {
    alert("First link this hardware definition to a catalog item in the Catalog tab.");
    setActiveDefinitionTab("catalog");
    return;
  }

  navigate(`/interior-editor/catalog/items/${itemId}/mounting`);
}

  if (loading) {
    return (
      <div className="h-[calc(100vh-80px)] flex items-center justify-center text-slate-500">
        Loading definition editor...
      </div>
    );
  }

    return (
    <div className="h-[calc(100vh-80px)] flex flex-col bg-slate-100 overflow-hidden">
      {/* HEADER */}
      <div className="flex items-center justify-between px-4 py-2 border-b bg-white">
        <div className="flex items-center gap-3">
          <div className="text-sm text-slate-500">DEFINITION EDITOR</div>
          <div className="text-lg font-semibold">
            {editName || definition?.name}
          </div>
          <span
            className={`px-2 py-0.5 text-xs rounded ${nodeTypeBadgeClass(
              editNodeType
            )}`}
          >
            {editNodeType}
          </span>
        </div>

        {!formValidation.ok && (
  <div className="bg-red-50 border-b border-red-300 px-4 py-2 text-xs text-red-700">
    {formValidation.errors.map((e, i) => (
      <div key={i}>
        <b>{e.key}</b>: {e.message} — {String(e.raw)}
      </div>
    ))}
  </div>
)}

        <div className="flex items-center gap-2">
          <HeaderButton onClick={() => navigate("/interior-editor/library")}>
            Library
          </HeaderButton>
          <HeaderButton onClick={() => navigate("/interior-editor/assets")}>
            Assets
          </HeaderButton>
          <HeaderButton onClick={() => navigate("/interior-editor/catalog")}>
            Catalog
          </HeaderButton>
          {editNodeType === "hardware" && (
  <>
    <HeaderButton onClick={() => setActiveDefinitionTab("catalog")}>
      Link Item
    </HeaderButton>

    <HeaderButton onClick={openMountingEditor}>
      Edit Mounting
    </HeaderButton>
  </>
)}
          <HeaderButton onClick={() => setShowParameterManager(true)}>
  Parameters
</HeaderButton>
<HeaderButton onClick={() => setShowMountingDrillings((v) => !v)}>
  {showMountingDrillings ? "Hide Drillings" : "Show Drillings"}
</HeaderButton>
<HeaderButton onClick={() => setShowCoordinateSystems((v) => !v)}>
  {showCoordinateSystems ? "Hide Origins" : "Show Origins"}
</HeaderButton>

          <HeaderButton onClick={() => navigate(-1)}>Back</HeaderButton>

          <HeaderButton danger onClick={handleDeleteDefinition}>
            Delete
          </HeaderButton>

          <button
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-1.5 rounded bg-green-600 text-white text-sm"
          >
            {saving ? "Saving..." : "Save"}
          </button>
        </div>
      </div>

      {/* MAIN GRID */}
      <div className="flex-1 min-h-0 grid grid-cols-[320px_1fr_380px]">
        {/* LEFT PANEL */}
        <div className="border-r bg-white flex flex-col min-h-0">
          {/* Tabs */}
          <div className="flex border-b px-2">
  <TabButton
    active={leftTab === "tree"}
    onClick={() => setLeftTab("tree")}
  >
    Hierarchy
  </TabButton>

  <TabButton
    active={leftTab === "resolved"}
    onClick={() => setLeftTab("resolved")}
  >
    Resolved
  </TabButton>

  <TabButton
    active={leftTab === "add"}
    onClick={() => setLeftTab("add")}
  >
    Add
  </TabButton>
</div>

          {/* Content */}
          <div className="flex-1 min-h-0 overflow-hidden">
            {leftTab === "tree" && (
              <div className="h-full flex flex-col">
                <ModuleChildrenTreePanel
                  nodes={children}
                  selectedId={selectedId}
                  onSelect={(id) => {
  setSelectedId(id);
  setResolvedNode(null);
}}
                />

                <div className="p-2 border-t flex gap-2">
                  <button
                    onClick={handleOpenAddChild}
                    className="flex-1 px-2 py-1.5 border rounded text-sm bg-white"
                  >
                    + Add child
                  </button>

                  <button
                    onClick={removeSelectedChild}
                    className="px-2 py-1.5 border rounded text-sm text-red-600"
                  >
                    Remove
                  </button>
                </div>
              </div>
            )}

            {leftTab === "resolved" && (
  <div className="h-full flex flex-col">
    <div className="px-3 py-2 border-b bg-slate-50">
      <div className="text-sm font-semibold">Resolved / Linked Structure</div>
      <div className="text-xs text-slate-500">
        Generated from linked catalog item, assembly, mounting and assets.
      </div>
    </div>

    <div className="flex-1 min-h-0 overflow-auto p-2 space-y-1">
      {visibleResolvedNodes.length === 0 ? (
        <div className="rounded border bg-slate-50 p-3 text-sm text-slate-500">
          No resolved linked elements.
        </div>
      ) : (
        visibleResolvedNodes.map((node) => {
          const isSelected = String(selectedId) === String(node.id);

          return (
            <button
              key={node.id}
              type="button"
              onClick={() => {
                setSelectedId(node.id);
                setResolvedNode(node);
                setActiveDefinitionTab("child");
              }}
              onDoubleClick={() => openResolvedNodeEditor(node, "auto")}
              className={[
                "w-full text-left rounded border px-2 py-2 text-sm",
                isSelected
                  ? "bg-blue-50 border-blue-300"
                  : "bg-white border-slate-200 hover:bg-slate-50",
              ].join(" ")}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="truncate font-medium">
                  {node.name || node.role || node.nodeType || "Element"}
                </div>

                <span className="text-[10px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-500">
                  {node.nodeType || "node"}
                </span>
              </div>

              <div className="text-xs text-slate-500 truncate">
                {node.category || node.role || node.id}
              </div>
            </button>
          );
        })
      )}
    </div>

    <div className="p-2 border-t flex gap-2">
      <button
        type="button"
        disabled={!resolvedNode}
        onClick={() => openResolvedNodeEditor(resolvedNode, "auto")}
        className="flex-1 px-2 py-1.5 border rounded text-sm bg-white disabled:opacity-40"
      >
        Open Source
      </button>

      <button
        type="button"
        disabled={!resolvedNode}
        onClick={() => {
          alert(
            "This element is generated from linked item / mounting. Open Source to edit or remove it."
          );
        }}
        className="px-2 py-1.5 border rounded text-sm text-slate-500 disabled:opacity-40"
      >
        Remove
      </button>
    </div>
  </div>
)}

            {leftTab === "add" && (
              <DefinitionPicker
                groupedDefinitions={groupedDefinitions}
                definitionSearch={definitionSearch}
                setDefinitionSearch={setDefinitionSearch}
                onAdd={addChildByDefinition}
                onOpen={openDefinitionEditorById}
              />
            )}
          </div>
        </div>

        {/* CENTER - 3D */}
        <div className="relative bg-slate-200">
          <HierarchySceneCanvas
  rootInstances={sceneRootInstances}
  definitions={allDefinitions}
  globalParameters={PREVIEW_PROJECT_PARAMETERS}
  selectedId={selectedId}
  selectedPartition={selectedPartition}
  onSelectPartition={setSelectedPartition}
  showMountingDrillings={showMountingDrillings}
  showCoordinateSystems={showCoordinateSystems}
  onDimensionPick={onDimensionPick}
  dimensionDraft={dimensionDraft}
  onSelect={(id) => {
    if (id === "__definition_preview__") return;
    setSelectedId(id);
    setResolvedNode(null);
  }}
  onResolvedSelect={(node) => {
    if (String(node?.id) === "__definition_preview__") return;
    setResolvedNode(node);
  }}
  onDoubleOpen={(node) => {
    openResolvedNodeEditor(node, "auto");
  }}
  onChangeNode={(patch) => {
    setChildren((prev) =>
      updateNodeInTree(prev, patch.id, (node) => ({
        ...node,
        ...patch,
      }))
    );
    setResolvedNode(null);
  }}
/>
        </div>

        {/* RIGHT PANEL */}
        <div className="border-l bg-white flex flex-col min-h-0">
          <div className="flex border-b px-2 overflow-auto">
            <TabButton
              active={activeDefinitionTab === "general"}
              onClick={() => setActiveDefinitionTab("general")}
            >
              General
            </TabButton>

            <TabButton
              active={activeDefinitionTab === "properties"}
              onClick={() => setActiveDefinitionTab("properties")}
            >
              Properties
            </TabButton>

            <TabButton
              active={activeDefinitionTab === "catalog"}
              onClick={() => setActiveDefinitionTab("catalog")}
            >
              Catalog
            </TabButton>

            <TabButton
              active={activeDefinitionTab === "child"}
              onClick={() => setActiveDefinitionTab("child")}
            >
              Child
            </TabButton>
          </div>

          <div className="flex-1 min-h-0 overflow-auto p-3">
            {activeDefinitionTab === "general" && (
              <DefinitionPropertiesInspector
                name={editName}
                code={editCode}
                description={editDescription}
                nodeType={editNodeType}
                category={editCategory}
                geometryType={editGeometryType}
                onChangeName={setEditName}
                onChangeCode={setEditCode}
                onChangeDescription={setEditDescription}
                onChangeNodeType={setEditNodeType}
                onChangeCategory={setEditCategory}
                onChangeGeometryType={setEditGeometryType}
              />
            )}

            {activeDefinitionTab === "properties" && (
              <DefinitionParametersInspector
                parameters={editDefaultParameters}
                onChange={setEditDefaultParameters}
              />
            )}

            {activeDefinitionTab === "catalog" && (
  <div className="space-y-3">
    <div className="border rounded p-3 bg-white space-y-3">
      <div>
        <div className="font-semibold text-sm">Catalog item link</div>
        <div className="text-xs text-slate-500">
          Link this hardware definition to a catalog item / asset / GLB.
        </div>
      </div>

      <input
  value={catalogSearch}
  onChange={(e) => setCatalogSearch(e.target.value)}
  placeholder="Search catalog item by name, code, SKU, brand..."
  className="w-full px-3 py-2 border rounded text-sm"
/>

{catalogSearching && (
  <div className="text-xs text-slate-500">Searching...</div>
)}

<select
  value={editCatalogItemId || ""}
  onChange={(e) => setEditCatalogItemId(e.target.value)}
  className="w-full px-3 py-2 border rounded text-sm bg-white"
>
  <option value="">No item</option>

  {catalogSelectItems.map((item) => (
    <option key={item.id} value={item.id}>
      {item.name || item.code} · {item.sku || "—"} · {item.brand || item.manufacturer || "—"} · id {item.id}
    </option>
  ))}
</select>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={() => navigate("/interior-editor/catalog")}
          className="px-3 py-1.5 border rounded bg-white text-sm"
        >
          Open Catalog
        </button>

        <button
          type="button"
          disabled={!editCatalogItemId && !definition?.linkedItem?.id}
          onClick={openMountingEditor}
          className="px-3 py-1.5 border rounded bg-blue-50 text-blue-700 text-sm disabled:opacity-40"
        >
          Edit Mounting
        </button>

        <button
          type="button"
          onClick={() => setEditCatalogItemId("")}
          className="px-3 py-1.5 border rounded bg-white text-red-600 text-sm"
        >
          Unlink
        </button>
      </div>
    </div>

    <LinkedCatalogCard
      item={selectedCatalogItem || definition?.linkedItem}
      navigate={navigate}
    />
  </div>
)}

            {activeDefinitionTab === "child" && (
  <div className="space-y-3">
    {!selectedNode && resolvedNode ? (
      <div className="rounded border bg-amber-50 p-3 text-sm text-amber-800">
        <div className="font-semibold">Generated linked element</div>
        <div className="text-xs mt-1">
          This element is generated from catalog item, asset assembly, or mounting.
          Open source editor to modify or remove it.
        </div>

        <button
          type="button"
          onClick={() => openResolvedNodeEditor(resolvedNode, "auto")}
          className="mt-3 px-3 py-1.5 rounded border bg-white text-sm"
        >
          Open Source
        </button>
      </div>
    ) : null}

    <RightPanel
      selectedNode={selectedNode}
      resolvedNode={resolvedNode}
      projectParameters={editDefaultParameters}
      setProjectParameters={setEditDefaultParameters}
      onChangeNode={updateSelectedNode}
      selectedPartition={selectedPartition}
    />
  </div>
)}
          </div>
        </div>
      </div>

      {/* DIALOG */}
      {showAddChildDialog && (
        <AddChildDialog
          open={showAddChildDialog}
          onClose={() => setShowAddChildDialog(false)}
          definitions={availableDefinitions}
          onPick={handlePickChildDefinition}
        />
      )}

      {showParameterManager && (
 <ParameterManagerModal
  isOpen={showParameterManager}
  onClose={() => setShowParameterManager(false)}
  resolvedNode={
    resolvedNode
      ? {
          ...resolvedNode,
          node: selectedNode || resolvedNode,
        }
      : {
          name: definition?.name || "Definition",
          effectiveParameters: editDefaultParameters,
          resolvedParameters: Object.entries(editDefaultParameters || {}).map(
            ([key, value]) => ({
              key,
              value,
              source: "type",
            })
          ),
          parameterDebug: {
            definitionDefaults: editDefaultParameters,
            effectiveParameters: editDefaultParameters,
          },
          node: null,
          definition: definition,
        }
  }
  projectParameters={editDefaultParameters}
  onChangeProjectParameters={setEditDefaultParameters}
  onChangeTypeParameters={setEditDefaultParameters}
  onUpdateNode={(nodeId, patch) => {
    setChildren((prev) =>
      updateNodeInTree(prev, nodeId, (node) => ({
        ...node,
        ...patch,
      }))
    );
    setResolvedNode(null);
  }}
/>
)}
    </div>
  );
}